/**
 * React hooks for real-time event subscriptions.
 *
 * These hooks provide a clean, React-idiomatic way to subscribe to
 * backend events with automatic cleanup on unmount.
 *
 * @example
 * function SessionList() {
 *   // Auto-refresh sessions when any session changes
 *   useRealtimeEvent('db_session_changed', (event) => {
 *     if (event.data.operation !== 'DELETE') {
 *       loadSessions()
 *     }
 *   })
 * }
 */

import { useEffect, useCallback, useRef } from 'react'
import {
  useRealtimeStore,
  type RealtimeEvent,
  type RealtimeEventType,
  type EventHandler,
  type DbChangeEvent,
} from '@/store/realtimeStore'

// ============================================================================
// Core Hook: Subscribe to a specific event type
// ============================================================================

/**
 * Subscribe to a specific realtime event type.
 * Automatically unsubscribes when the component unmounts.
 *
 * @param eventType - The event type to subscribe to
 * @param handler - Callback function when event is received
 * @param deps - Optional dependency array for the handler
 *
 * @example
 * useRealtimeEvent('db_session_changed', (event) => {
 *   console.log('Session updated:', event.data.id)
 * })
 */
export function useRealtimeEvent<T extends RealtimeEvent>(
  eventType: RealtimeEventType,
  handler: EventHandler<T>,
  deps: React.DependencyList = []
) {
  const subscribe = useRealtimeStore(state => state.subscribe)

  // Memoize the handler to prevent unnecessary re-subscriptions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedHandler = useCallback(handler, deps)

  useEffect(() => {
    const unsubscribe = subscribe(eventType, memoizedHandler)
    return unsubscribe
  }, [eventType, memoizedHandler, subscribe])
}

// ============================================================================
// Specialized Hooks for Common Patterns
// ============================================================================

/**
 * Subscribe to database change events for a specific table.
 * Useful for auto-refreshing lists when data changes.
 *
 * @param table - Table name to watch ('sessions', 'dev_tasks', 'feedback')
 * @param handler - Callback with operation type and record data
 *
 * @example
 * useDbChange('sessions', (operation, id, data) => {
 *   if (operation === 'INSERT' || operation === 'UPDATE') {
 *     refreshSessions()
 *   }
 * })
 */
export function useDbChange(
  table: 'sessions' | 'dev_tasks' | 'feedback',
  handler: (operation: 'INSERT' | 'UPDATE' | 'DELETE', id: string | number, data: Record<string, unknown>) => void,
  deps: React.DependencyList = []
) {
  const eventType = `db_${table === 'sessions' ? 'session' : table === 'dev_tasks' ? 'task' : 'feedback'}_changed` as RealtimeEventType

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedHandler = useCallback(handler, deps)

  useRealtimeEvent<DbChangeEvent>(
    eventType,
    (event) => {
      if (event.data.table === table) {
        memoizedHandler(event.data.operation, event.data.id, event.data.data)
      }
    },
    [memoizedHandler, table]
  )
}

/**
 * Subscribe to session updates specifically.
 * Fires when session titles, message counts, or other metadata changes.
 *
 * @example
 * useSessionUpdates((sessionId, data) => {
 *   updateSessionInList(sessionId, data)
 * })
 */
export function useSessionUpdates(
  handler: (sessionId: string, data: { summary?: string; updated_at?: string }) => void,
  deps: React.DependencyList = []
) {
  useDbChange(
    'sessions',
    (operation, id, data) => {
      if (operation !== 'DELETE') {
        handler(String(id), data as { summary?: string; updated_at?: string })
      }
    },
    deps
  )
}

/**
 * Subscribe to all events (useful for debugging or logging).
 *
 * @example
 * useAllEvents((event) => {
 *   console.log('[Event]', event.type, event.data)
 * })
 */
export function useAllEvents(
  handler: EventHandler,
  deps: React.DependencyList = []
) {
  const subscribeAll = useRealtimeStore(state => state.subscribeAll)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedHandler = useCallback(handler, deps)

  useEffect(() => {
    const unsubscribe = subscribeAll(memoizedHandler)
    return unsubscribe
  }, [memoizedHandler, subscribeAll])
}

// ============================================================================
// Connection Management Hook
// ============================================================================

/**
 * Initialize and manage the realtime connection.
 * Should be called once at the app level (in App.tsx or a provider).
 *
 * @returns Connection state and control functions
 *
 * @example
 * function App() {
 *   const { connectionState, isConnected } = useRealtimeConnection()
 *
 *   return (
 *     <div>
 *       <ConnectionIndicator state={connectionState} />
 *       <MainContent />
 *     </div>
 *   )
 * }
 */
export function useRealtimeConnection() {
  const connect = useRealtimeStore(state => state.connect)
  const disconnect = useRealtimeStore(state => state.disconnect)
  const connectionState = useRealtimeStore(state => state.connectionState)
  const eventsReceived = useRealtimeStore(state => state.eventsReceived)
  const lastEventTime = useRealtimeStore(state => state.lastEventTime)
  const reconnectAttempts = useRealtimeStore(state => state.reconnectAttempts)

  // Auto-connect on mount, disconnect on unmount
  useEffect(() => {
    connect()
    return () => {
      disconnect()
    }
  }, [connect, disconnect])

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    isReconnecting: connectionState === 'reconnecting',
    eventsReceived,
    lastEventTime,
    reconnectAttempts,
    connect,
    disconnect,
  }
}

// ============================================================================
// Debounced Event Hook (for high-frequency events)
// ============================================================================

/**
 * Subscribe to events with debouncing to prevent excessive re-renders.
 * Useful for high-frequency events like streaming chunks.
 *
 * @param eventType - Event type to subscribe to
 * @param handler - Handler called with accumulated events after debounce
 * @param debounceMs - Debounce delay in milliseconds
 *
 * @example
 * useDebouncedRealtimeEvent('response_chunk', (events) => {
 *   const text = events.map(e => e.data.chunk).join('')
 *   setResponse(prev => prev + text)
 * }, 50)
 */
export function useDebouncedRealtimeEvent<T extends RealtimeEvent>(
  eventType: RealtimeEventType,
  handler: (events: T[]) => void,
  debounceMs: number = 100,
  deps: React.DependencyList = []
) {
  const subscribe = useRealtimeStore(state => state.subscribe)
  const eventsRef = useRef<T[]>([])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedHandler = useCallback(handler, deps)

  useEffect(() => {
    const unsubscribe = subscribe<T>(eventType, (event) => {
      eventsRef.current.push(event)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        if (eventsRef.current.length > 0) {
          memoizedHandler([...eventsRef.current])
          eventsRef.current = []
        }
      }, debounceMs)
    })

    return () => {
      unsubscribe()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [eventType, memoizedHandler, debounceMs, subscribe])
}

// ============================================================================
// Conditional Event Hook (filter events)
// ============================================================================

/**
 * Subscribe to events that match a filter condition.
 *
 * @example
 * useFilteredRealtimeEvent(
 *   'db_session_changed',
 *   (event) => event.data.id === currentSessionId,
 *   (event) => setSessionData(event.data.data)
 * )
 */
export function useFilteredRealtimeEvent<T extends RealtimeEvent>(
  eventType: RealtimeEventType,
  filter: (event: T) => boolean,
  handler: EventHandler<T>,
  deps: React.DependencyList = []
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFilter = useCallback(filter, deps)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedHandler = useCallback(handler, deps)

  useRealtimeEvent<T>(
    eventType,
    (event) => {
      if (memoizedFilter(event)) {
        memoizedHandler(event)
      }
    },
    [memoizedFilter, memoizedHandler]
  )
}
